import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Award, Clock, Users, Building2, Shield, Phone, Mail, MapPin, ChevronLeft, Quote } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { COMPANY_INFO, STATS } from '@/lib/index';

const SERVICES = [
  { icon: '⛏️', title: 'الحفر والأساسات', desc: 'أعمال الحفر الآلي والأساسات الهندسية والخوازيق وفق أعلى المعايير.', items: ['الحفر الآلي', 'دراسات التربة', 'الأساسات العميقة', 'الجدران الساندة'] },
  { icon: '🏗️', title: 'البناء الهيكلي', desc: 'هياكل خرسانية وحديدية للفنادق الفاخرة بأحدث تقنيات البناء.', items: ['الهياكل الخرسانية', 'الأعمال الحديدية', 'الجدران الإنشائية', 'الأسقف والبلاطات'] },
  { icon: '✨', title: 'التشطيبات الداخلية', desc: 'تشطيبات داخلية راقية بمواد عالمية وحرفية متميزة تليق بالفنادق الفاخرة.', items: ['الديكورات الفاخرة', 'الأرضيات والجدران', 'الأسقف المعلقة', 'أنظمة الإضاءة'] },
  { icon: '🏛️', title: 'التشطيبات الخارجية', desc: 'واجهات خارجية فاخرة تنسجم مع الطابع المعماري الراقي للمنطقة.', items: ['الواجهات الزجاجية', 'الكسوات الحجرية', 'المناطق المكشوفة', 'التنسيق الخارجي'] },
  { icon: '🔧', title: 'الأعمال الميكانيكية', desc: 'تنفيذ كامل لأعمال M&E بما يشمل التكييف والصرف الصحي والكهرباء.', items: ['أنظمة التكييف', 'الكهرباء والإنارة', 'السباكة والصرف', 'الحريق والأمن'] },
  { icon: '🗝️', title: 'تسليم المفتاح', desc: 'ندير المشروع بالكامل من الصفر حتى التسليم التام بجودة واحترافية عالية.', items: ['إدارة المشاريع', 'مراقبة الجودة', 'الجداول الزمنية', 'التسليم والتشغيل'] },
];

const STRENGTHS = [
  { Icon: Users, title: 'فريق هندسي متخصص', desc: 'نمتلك فريقاً هندسياً وفنياً وإدارياً من نخبة المهندسين ذوي الخبرة الواسعة.' },
  { Icon: Clock, title: 'الالتزام بالوقت', desc: 'نضمن تسليم المشاريع في الوقت المحدد دون المساس بالجودة.' },
  { Icon: Award, title: 'معايير جودة عالمية', desc: 'نطبق أعلى معايير الجودة في جميع مراحل التنفيذ.' },
  { Icon: Shield, title: 'شبكة موردين معتمدة', desc: 'شبكة واسعة من مقاولي الباطن والموردين المحترفين المعتمدين.' },
  { Icon: MapPin, title: 'خبرة محلية متعمقة', desc: 'خبرة واسعة في العمل بالمنطقة المركزية بالمدينة المنورة.' },
  { Icon: Building2, title: 'شراكات استراتيجية', desc: 'علاقات راسخة مع كبار المطورين العقاريين وصناديق الاستثمار.' },
];

const PROJECTS = [
  { title: 'فندق فاخر - المنطقة المركزية', loc: 'قرب المسجد النبوي', type: 'فندق 5 نجوم', status: 'مكتمل', img: '/images/luxury_hotel_construction_madinah_skyscr_7.jpeg' },
  { title: 'برج فندقي راقي', loc: 'وسط المدينة المنورة', type: 'فندق وأجنحة', status: 'مكتمل', img: '/images/luxury_hotel_interior_3.jpeg' },
  { title: 'مجمع فندقي متكامل', loc: 'المنطقة المركزية', type: 'مجمع فندقي', status: 'قيد التنفيذ', img: '/images/luxury_hotel_building_construction_site__6.jpeg' },
];

// Animated Counter
function Counter({ target, suffix, label, delay }: { target: number; suffix: string; label: string; delay: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let s: number | null = null;
    const step = (ts: number) => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / 1800, 1);
      setCount(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target]);

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }} viewport={{ once: true }}
      className="text-center">
      <div className="text-4xl sm:text-5xl font-black text-navy mb-1 tabular-nums" style={{ fontFamily: 'Tajawal,sans-serif' }}>
        {count}{suffix}
      </div>
      <div className="text-muted-foreground text-sm font-medium">{label}</div>
    </motion.div>
  );
}

// Contact Form
function ContactForm() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', project: '', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
    setForm({ name: '', phone: '', email: '', project: '', message: '' });
  };
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-border p-6 sm:p-8">
      <h3 className="text-xl font-bold text-navy mb-6">احصل على عرض سعر مجاني</h3>
      <AnimatePresence>
        {sent && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
            ✅ تم إرسال رسالتك! سنتواصل معك قريباً.
          </motion.div>
        )}
      </AnimatePresence>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-muted-foreground mb-1.5 font-medium">الاسم *</label>
            <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="اسمك الكامل"
              className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-sm focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 transition-all" />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1.5 font-medium">الجوال *</label>
            <input required type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+966 5X XXX XXXX" dir="ltr"
              className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-sm focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 transition-all" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-muted-foreground mb-1.5 font-medium">البريد الإلكتروني</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" dir="ltr"
            className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-sm focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 transition-all" />
        </div>
        <div>
          <label className="block text-sm text-muted-foreground mb-1.5 font-medium">نوع المشروع</label>
          <select value={form.project} onChange={e => set('project', e.target.value)}
            className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-sm focus:outline-none focus:border-navy transition-all">
            <option value="">اختر نوع المشروع</option>
            <option>فندق فاخر</option><option>فندق وأجنحة</option><option>مجمع فندقي</option><option>أخرى</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-muted-foreground mb-1.5 font-medium">تفاصيل المشروع</label>
          <textarea rows={4} value={form.message} onChange={e => set('message', e.target.value)} placeholder="اكتب تفاصيل مشروعك..."
            className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground text-sm focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 transition-all resize-none" />
        </div>
        <button type="submit"
          className="w-full py-4 bg-navy text-white rounded-xl font-bold text-base hover:bg-navy-light transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2">
          إرسال الطلب
          <ChevronLeft size={18} />
        </button>
      </form>
    </div>
  );
}

export default function Home() {
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Navbar />

      {/* ===== HERO ===== */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src="/images/luxury_hotel_construction_madinah_skyscr_7.jpeg" alt="المدينة المنورة" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/75 via-navy/55 to-navy/80" />
        </div>
        <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-l from-gold via-gold-light to-gold" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 border border-gold/40 rounded-full text-gold text-sm font-medium mb-8 backdrop-blur-sm">
              <Star size={13} className="fill-gold" />
              <span>متخصصون في الفنادق الفاخرة بالمدينة المنورة</span>
              <Star size={13} className="fill-gold" />
            </div>
            <div className="flex justify-center mb-6">
              <img src="/images/logo_ibs_enhanced.png" alt="IBS" className="h-28 w-28 object-contain drop-shadow-2xl bg-white/10 rounded-2xl p-2 backdrop-blur-sm" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              أنظمة المباني <span className="text-gold">المبتكرة</span>
            </h1>
            <p className="text-white/80 text-xl mb-3 font-medium">للمقاولات • Innovative Building Systems</p>
            <p className="text-2xl sm:text-3xl font-bold text-gold mb-8" style={{ fontFamily: 'Amiri,serif' }}>
              "نبني الفخامة... نسلّم الإتقان"
            </p>
            <p className="text-white/85 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              {COMPANY_INFO.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => scrollTo('contact')}
                className="px-8 py-4 bg-gold text-white rounded-xl font-bold text-lg hover:bg-gold/90 hover:shadow-2xl hover:scale-105 transition-all duration-300 w-full sm:w-auto">
                احصل على عرض سعر
              </button>
              <button onClick={() => scrollTo('services')}
                className="px-8 py-4 bg-white/15 border border-white/40 text-white rounded-xl font-semibold text-lg hover:bg-white/25 transition-all duration-300 backdrop-blur-sm w-full sm:w-auto">
                اكتشف خدماتنا
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-gold/60 rounded-full flex items-start justify-center p-1.5">
            <motion.div animate={{ y: [0, 12, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-gold rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-16 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
            {STATS.map((s, i) => <Counter key={i} target={s.value} suffix={s.suffix} label={s.label} delay={i * 0.1} />)}
          </div>
        </div>
      </section>

      {/* ===== CEO MESSAGE ===== */}
      <section id="ceo" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* CEO Photo */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}
              className="relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="/images/ceo_osama.png" alt="الشيخ أسامة المسعود" className="w-full h-[480px] object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl border border-border p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center">
                  <Award size={22} className="text-gold" />
                </div>
                <div>
                  <div className="text-navy font-bold text-sm">الشيخ أسامة المسعود</div>
                  <div className="text-muted-foreground text-xs">المؤسس والرئيس التنفيذي</div>
                </div>
              </div>
            </motion.div>

            {/* CEO Quote */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}
              className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/8 border border-navy/15 rounded-full text-navy text-sm mb-6">
                <Quote size={14} />
                <span>كلمة الرئيس التنفيذي</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-navy mb-6" style={{ fontFamily: 'Tajawal,sans-serif' }}>
                رسالة <span className="text-gold">المؤسس</span>
              </h2>
              <div className="relative mb-8">
                <Quote size={48} className="text-gold/15 absolute -top-2 -right-2" />
                <blockquote className="text-foreground text-lg leading-relaxed pr-8 border-r-4 border-gold font-medium" style={{ fontFamily: 'Amiri,serif' }}>
                  "لقد آمنّا منذ اليوم الأول بأن المدينة المنورة تستحق أفضل ما يمكن بناؤه، لا لكونها مجرد وجهة استثمارية، بل لأنها تحمل في جوهرها قيمة روحية وحضارية لا تُضاهى. من هذا الإيمان وُلدت شركة أنظمة المباني المبتكرة."
                </blockquote>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-6">
                تحت قيادة الشيخ أسامة المسعود وبدعم من شركة ون للاستثمار، نسعى دائماً إلى تجاوز التوقعات وتقديم مشاريع تجمع بين الفخامة والجودة والالتزام بالمواعيد. فريقنا المتخصص يعمل بشغف وانضباط لتحويل كل مشروع إلى إنجاز يليق بالمكان وأهله.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-navy rounded-xl flex items-center justify-center">
                  <img src="/images/logo_ibs_enhanced.png" alt="IBS" className="h-12 w-12 object-contain" />
                </div>
                <div>
                  <div className="text-navy font-black text-lg" style={{ fontFamily: 'Tajawal,sans-serif' }}>الشيخ أسامة المسعود</div>
                  <div className="text-gold font-semibold text-sm">المؤسس والرئيس التنفيذي</div>
                  <div className="text-muted-foreground text-xs">شركة أنظمة المباني المبتكرة للمقاولات</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}
              className="relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img src="/images/luxury_hotel_interior_3.jpeg" alt="مشاريعنا" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gold/15 rounded-xl flex items-center justify-center">
                    <Star size={22} className="text-gold fill-gold" />
                  </div>
                  <div>
                    <div className="text-gold font-bold text-sm">شركة موثوقة</div>
                    <div className="text-muted-foreground text-xs">15+ سنة من التميز</div>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}
              className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/8 border border-navy/15 rounded-full text-navy text-sm mb-6">
                <Building2 size={14} />
                <span>من نحن</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-navy mb-6" style={{ fontFamily: 'Tajawal,sans-serif' }}>
                شريكك الموثوق في <span className="text-gold">بناء الفنادق الفاخرة</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6 text-base">{COMPANY_INFO.description}</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-surface border border-border rounded-xl p-4">
                  <Star size={16} className="text-gold fill-gold mb-2" />
                  <h4 className="text-navy font-bold text-sm mb-1">رؤيتنا</h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">{COMPANY_INFO.vision}</p>
                </div>
                <div className="bg-surface border border-border rounded-xl p-4">
                  <Shield size={16} className="text-navy mb-2" />
                  <h4 className="text-navy font-bold text-sm mb-1">مهمتنا</h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">{COMPANY_INFO.mission}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-muted-foreground">تحت إشراف:</span>
                {COMPANY_INFO.owners.map((o, i) => (
                  <span key={i} className="px-3 py-1.5 bg-navy text-white text-xs rounded-full font-semibold">{o}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}
            className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/8 border border-navy/15 rounded-full text-navy text-sm mb-5">
              <Building2 size={14} /><span>خدماتنا</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-navy mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              خدمات <span className="text-gold">متكاملة</span> من الألف إلى الياء
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">ننفذ جميع مراحل البناء بخبرة واحترافية، من الحفر حتى تسليم المفتاح</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }} viewport={{ once: true }}
                className="group bg-white border border-border rounded-2xl p-6 hover:border-navy/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="text-4xl mb-4">{s.icon}</div>
                <h3 className="text-lg font-bold text-navy mb-2 group-hover:text-gold transition-colors">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{s.desc}</p>
                <ul className="space-y-1.5">
                  {s.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROJECTS ===== */}
      <section id="projects" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}
            className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/8 border border-navy/15 rounded-full text-navy text-sm mb-5">
              <Building2 size={14} /><span>مشاريعنا</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-navy mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              أبرز <span className="text-gold">مشاريعنا</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">سجل حافل من الإنجازات في بناء الفنادق الفاخرة بالمدينة المنورة</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {PROJECTS.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.12 }} viewport={{ once: true }}
                className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-border">
                <div className="relative h-64 overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
                  <div className="absolute top-4 right-4 flex gap-2">
                    <span className={`px-2.5 py-1 text-xs rounded-full font-semibold ${p.status === 'مكتمل' ? 'bg-green-100 text-green-700' : 'bg-gold/20 text-gold border border-gold/30'}`}>
                      {p.status}
                    </span>
                  </div>
                </div>
                <div className="p-5 bg-white">
                  <h3 className="text-navy font-bold text-base mb-1">{p.title}</h3>
                  <p className="text-xs text-gold font-medium mb-1">{p.type}</p>
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                    <MapPin size={11} />{p.loc}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['/images/luxury_hotel_building_construction_site__1.jpeg', '/images/luxury_hotel_interior_6.jpeg', '/images/luxury_hotel_building_construction_site__5.jpeg', '/images/luxury_hotel_interior_2.jpeg'].map((img, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }}
                className="rounded-xl overflow-hidden h-28 shadow-sm border border-border">
                <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STRENGTHS ===== */}
      <section id="strengths" className="py-24 bg-surface relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg,transparent,transparent 40px,rgba(10,31,68,0.15) 40px,rgba(10,31,68,0.15) 41px)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}
            className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/8 border border-navy/15 rounded-full text-navy text-sm mb-5">
              <Award size={14} /><span>نقاط قوتنا</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-navy mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              لماذا تختار <span className="text-gold">IBS</span>؟
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">نقدم ما يميّزنا من خلال الاحترافية والجودة التي لا تُضاهى</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STRENGTHS.map(({ Icon, title, desc }, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }} viewport={{ once: true }}
                className="group relative bg-white border border-border rounded-2xl p-6 hover:border-navy/30 hover:shadow-xl transition-all duration-300">
                <div className="absolute top-0 right-0 left-0 h-0.5 bg-gradient-to-l from-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-t-2xl" />
                <div className="w-13 h-13 bg-navy/8 border border-navy/12 rounded-xl flex items-center justify-center mb-4 group-hover:bg-navy transition-colors duration-300">
                  <Icon size={22} className="text-navy group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-navy font-bold text-base mb-2 group-hover:text-gold transition-colors">{title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}
            className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/8 border border-navy/15 rounded-full text-navy text-sm mb-5">
              <Phone size={14} /><span>تواصل معنا</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-navy mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              ابدأ مشروعك <span className="text-gold">معنا اليوم</span>
            </h2>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">فريقنا الهندسي مستعد لدراسة مشروعك وتقديم أفضل الحلول والعروض.</p>
              <div className="space-y-4">
                {[
                  { Icon: Phone, label: 'اتصل بنا', val: COMPANY_INFO.phone, href: `tel:${COMPANY_INFO.phone}`, dir: 'ltr' as const },
                  { Icon: Mail, label: 'البريد الإلكتروني', val: COMPANY_INFO.email, href: `mailto:${COMPANY_INFO.email}`, dir: 'ltr' as const },
                  { Icon: MapPin, label: 'الموقع', val: COMPANY_INFO.location, href: '#', dir: 'rtl' as const },
                ].map(({ Icon, label, val, href, dir }, i) => (
                  <a key={i} href={href}
                    className="flex items-center gap-4 p-4 bg-white border border-border rounded-xl hover:border-navy/30 hover:shadow-md transition-all duration-200 group">
                    <div className="w-12 h-12 bg-navy/8 rounded-xl flex items-center justify-center group-hover:bg-navy group-hover:text-white transition-all duration-300">
                      <Icon size={20} className="text-navy group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
                      <div className="text-navy font-semibold text-sm" dir={dir}>{val}</div>
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
